import React, {useCallback, useEffect, useRef, useState, useMemo} from "react";
import {
  Box,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  List,
  ListItem,
  useColorMode,
  useBreakpointValue,
} from "@chakra-ui/react";
import {CloseIcon, SearchIcon} from "@chakra-ui/icons";

const SearchBar = ({ items=[], onSearch, onSuggestionClick, onInputChange, placeholder = "Search...", initialValue = "", ...props }) => {
  const [query, setQuery] = useState(initialValue);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { colorMode } = useColorMode();
  const searchBarRef = useRef(null);

  // Responsive values
  const inputSize = useBreakpointValue({ base: "md", md: "lg" });
  const fontSize = useBreakpointValue({ base: "sm", md: "md" });

  // Update query when initialValue changes
  useEffect(() => {
    setQuery(initialValue);
  }, [initialValue]);

  // Directly compute suggestions with useMemo - no need for separate state
  const suggestions = useMemo(() => {
    if (!query.trim()) {
      return [];
    }
    return items.filter(item =>
        item.toLowerCase().includes(query.toLowerCase())
    );
  }, [query, items]);

  const handleInputChange = useCallback((e) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    setShowSuggestions(true);

    // Call onInputChange for real-time input handling (parent handles debouncing)
    if (onInputChange) {
      onInputChange(newQuery);
    }
  }, [onInputChange]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      if (onSearch) {
        onSearch(query);
      }
      setShowSuggestions(false);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  }, [query, onSearch]);

  const handleSuggestionClick = useCallback((suggestion) => {
    setQuery(suggestion);
    setShowSuggestions(false);

    if (onSearch) {
      onSearch(suggestion);
    }
    if (onSuggestionClick) {
      onSuggestionClick(suggestion);
    }
  }, [onSearch, onSuggestionClick]);

  const clearSearch = useCallback(() => {
    setQuery('');
    setShowSuggestions(false);

    if (onSearch) {
      onSearch('');
    }
  }, [onSearch]);

  const handleClickOutside = useCallback((event) => {
    if (searchBarRef.current && !searchBarRef.current.contains(event.target)) {
      setShowSuggestions(false);
    }
  }, []);

  const handleInputFocus = useCallback(() => {
    if (query) {
      setShowSuggestions(true);
    }
  }, [query]);



  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  const suggestionBg = colorMode === "light" ? "whiteAlpha.900" : "gray.700";
  const suggestionHoverBg = colorMode === "light" ? "gray.100" : "gray.600";

  return (
      <Box position="relative" ref={searchBarRef} {...props}>
        <InputGroup mb="4">
          <InputLeftElement 
            pointerEvents="none"
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <SearchIcon color="gray.500" boxSize={4} />
          </InputLeftElement>
          <Input
              type="text"
              placeholder={placeholder}
              value={query}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={handleInputFocus}
              variant="filled"
              borderRadius="full"
              size={inputSize}
              fontSize={fontSize}
              pl={10} // Add left padding to account for icon
              _placeholder={{
                color: "gray.500",
                fontSize: fontSize,
              }}
          />
          {query && (
              <InputRightElement 
                height="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <IconButton
                    icon={<CloseIcon />}
                    size="sm"
                    onClick={clearSearch}
                    variant="ghost"
                    aria-label="Clear search"
                    boxSize={6}
                />
              </InputRightElement>
          )}
        </InputGroup>
        
        {/* Mobile-optimized suggestions */}
        {showSuggestions && suggestions.length > 0 && (
            <Box
                position="absolute"
                top="100%"
                left="0"
                right="0"
                zIndex="10"
                bg={suggestionBg}
                borderRadius="lg"
                boxShadow="lg"
                maxHeight={{ base: "200px", md: "300px" }}
                overflowY="auto"
                mt={2}
            >
              <List spacing={0}>
                {suggestions.slice(0, 10).map((suggestion, index) => (
                    <ListItem
                        key={`${suggestion}-${index}`}
                        p={{ base: 2, md: 3 }}
                        cursor="pointer"
                        _hover={{ bg: suggestionHoverBg }}
                        onClick={() => handleSuggestionClick(suggestion)}
                        fontSize={fontSize}
                    >
                      {suggestion}
                    </ListItem>
                ))}
              </List>
            </Box>
        )}
      </Box>
  );
};

export default SearchBar;
